import { environment } from "../environments/environment";
import { environmentProd } from "../environments/environment.prod";
export default class Utils {
  public static get PRIISM_APPT_API_ENDPOINT(): string {
    return (Utils.isProduction) ? environmentProd.priismApptApiEndPoint : environment.priismApptApiEndPoint;
  }

  public static get PRIISM_API_ENDPOINT(): string {
    return (Utils.isProduction) ? environmentProd.priismApiEndPoint : environment.priismApiEndPoint;
  }

  // Priism v2 API origin — used by src/api/v2.ts. The tenant is resolved from
  // ?tenant=<referenceCode>, so the same app serves every clinic.
  public static get PRIISM_V2_API_ENDPOINT(): string {
    return (Utils.isProduction) ? environmentProd.priismV2ApiEndPoint : environment.priismV2ApiEndPoint;
  }

  public static get RAZORPAY_KEY_ID(): string {
    return (Utils.isProduction) ? environmentProd.razorpayKeyId : environment.razorpayKeyId;
  }

  public static get RAZORPAY_KEY_ID_MANOSHANTI(): string {
    return (Utils.isProduction) ? environmentProd.razorpayKeyIdManoshanti : environment.razorpayKeyIdManoshanti;
  }

  public static get RAZORPAY_KEY_ID_STHIRA(): string {
    return (Utils.isProduction) ? environmentProd.razorpayKeyIdSthira : environment.razorpayKeyIdSthira;
  }

  public static get isProduction(): boolean {
    return process.env.NODE_ENV === 'development' ? false : true;
  }

  static PRIISM_WEB_URL = "https://priism.in/";
  // Public clinician-image endpoint on priism v2. The old CloudFront
  // (d3ipk6b7f792pn) fronts the LEGACY bucket and 403s v2 keys; v2 serves
  // profile images itself via this public, referenced-key-gated route.
  // Hit v2 directly (not the /api Flask proxy — it JSON-decodes responses
  // and would corrupt image bytes); <img> loads need no CORS.
  static IMAGE_URL = "https://priism.in/v1/public/booking/image?key=";

  // Resolve a stored image key → a fetchable URL. v2 returns
  // `profile_image_url` as a storage key (`<tenant>/user/...` or
  // `tenants/<tenant>/...`), but may also hand back a full URL. Pass an
  // absolute URL through untouched, otherwise serve the key through v2's
  // public image endpoint. Empty/blank → "" so callers fall back to the
  // default avatar.
  static assetUrl(key?: string | null): string {
    const t = (key ?? "").trim();
    if (!t) return "";
    if (/^https?:\/\//i.test(t)) return t;
    return Utils.IMAGE_URL + encodeURIComponent(t.replace(/^\/+/, ""));
  }

  static stringAvatar(name: string): {
    sx: { bgcolor: string };
    children: string;
  } {
    return {
      sx: {
        bgcolor: this.stringToColor(name),
      },
      children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
    };
  }

  static stringToColor(string: string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }

  static calculateAge(date: any) {
    var ageDifMs = Date.now() - date;
    var ageDate = new Date(ageDifMs); // miliseconds from epoch
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  static getCssClassNamesByReferecnceCode = (referenceCode) => {
    let classNames = {}
    // v2 tenant codes look like "manoshanti_bengaluru" / "sthira_…". Map to the
    // clinic theme by prefix so per-clinic styling still applies under v2.
    const code = (referenceCode || "").toUpperCase();
    const theme =
      code.startsWith("STHIRA") ? "STHIRA"
      : (code.startsWith("MANOSHANTI") || code.startsWith("MANOLLASA")) ? "MANOSHANTI"
      : "";
    switch (theme) {
      case "MANOSHANTI":
        classNames = {
          'homeBoxBGcolor': 'manoshanti-home-box',
          'toggleBtns': "manoshanti-designation-filter-btns",
          'profileName': "manoshanti-profile-name",
          'providerBookApptBtn': "manoshanti-provider-book-button",
          'profileViewBtn': "manoshanti-profile-view-btn",
          'providerSection': "manoshanti-provider-section-color",
          'buttonColor': "manoshanti-button-color",
          'buttonColorGlowingAnimation': "manoshanti-create-appointment-btn",
          'buttonHoverColor': "manoshanti-btn-hover",
          'cardBorderColor': "manoshanti-card-border",
          'SubHeadingColor': "manoshanti-sub-heading-color",
          'selectedDate': "manoshanti-date-selected",
          'selectedChip': "manoshanti-chip-selected",
          'bookedChip': "manoshanti-chip-booked",
          'blockedChip': "manoshanti-chip-blocked",
          'pastTimeChip': "manoshanti-chip-past-time",
          'selectedClinic': "manoshanti-clinic-selected",
          'selectedDuration': "manoshanti-duration-selected",
          'cards': "manoshanti-cards",
          'textColor': "manoshanti-text-color",
          'borderColor': "manoshanti-border-color",
          'tableHeader1': "manoshanti-table-header-1",
          'tableHeader2': "manoshanti-table-header-2"
        }
        break;
      case "STHIRA":
        classNames = {
          'homeBoxBGcolor': 'sthira-home-box',
          'toggleBtns': "sthira-designation-filter-btns",
          'profileName': "sthira-profile-name",
          'providerBookApptBtn': "sthira-provider-book-button",
          'profileViewBtn': "sthira-profile-view-btn",
          'providerSection': "sthira-provider-section-color",
          'buttonColor': "sthira-button-color",
          'buttonColorGlowingAnimation': "sthira-create-appointment-btn",
          'buttonHoverColor': "sthira-btn-hover",
          'cardBorderColor': "sthira-card-border",
          'subHeadingColor': "sthira-sub-heading-color",
          'selectedDate': "sthira-date-selected",
          'selectedChip': "sthira-chip-selected",
          'bookedChip': "sthira-chip-booked",
          'blockedChip': "sthira-chip-blocked",
          'pastTimeChip': "sthira-chip-past-time",
          'selectedClinic': "sthira-clinic-selected",
          'selectedDuration': "sthira-duration-selected",
          'cards': "sthira-cards",
          'textColor': "sthira-text-color",
          'borderColor': "sthira-border-color",
          'tableHeader1': "sthira-table-header-1",
          'tableHeader2': "sthira-table-header-2"
        }
        break;
    }
    return classNames;
  }

}
